import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { formatDate } from '@/shared/lib/dateUtils'
import { TituloDePantalla } from '@/shared/components/TituloDePantalla'
/**
 * Lista de Roles
 * Fecha: 16 Dic 2025
 *
 * Vista principal con tabla y paginación
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CirclePlus, Eye, Pen, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import { ConfirmarEliminacion } from '@/shared/components/ConfirmarEliminacion';
import EmptyIcon from '@/assets/empty-users-config.svg';
import { SwitchCustom } from '@/shared/components/Switch';
import type { Roles } from '@/types/roles.types';
import { useAuth } from '@/shared/hooks/useAuth';
import { rolesApi } from '@/features/configuracion/api/roles.api';
import {
  userHasPermission,
} from '@/shared/lib/roleUtils';
import PermissionsDenyPage from '@/pages/configuracion/roles/permissionsDenyPage';
import { EsqueletoTabla } from '@/shared/components/Esqueleto'

export const RolesTableView: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const token = localStorage.getItem("access_token");

  const [roles, setRoles] = useState<Roles[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);

  const [porEliminar, setPorEliminar] = useState<Roles | null>(null);
  const [eliminando, setEliminando] = useState(false);

  const totalPages = Math.ceil(total / limit);

  /**
   * El back es quien decide si se puede: un rol con usuarios, o uno del
   * sistema, responde 409 con el motivo ya redactado ("lo tienen 3 usuarios"),
   * y ese texto es el que se muestra. Si borrar este era lo último de la
   * página, se regresa a la anterior para no quedar en una vacía.
   */
  const eliminarRol = async () => {
    if (!porEliminar || !token) return;
    setEliminando(true);
    try {
      await rolesApi.remove(porEliminar.id, token);
      toast.success(`Se eliminó el rol ${porEliminar.name}`);
      const quedabaSolo = roles.length === 1 && page > 1;
      await getRolesTable(quedabaSolo ? page - 1 : page);
    } catch (error) {
      const mensaje =
        typeof error === "object" && error !== null && "message" in error
          ? String((error as { message: unknown }).message)
          : "No se pudo eliminar el rol. Intenta de nuevo.";
      toast.error(mensaje);
    } finally {
      setEliminando(false);
      setPorEliminar(null);
    }
  };

  const getRolesTable = async (currentPage = page) => {
    try {
      setIsLoading(true);

      if (user && token) {
        const res = await rolesApi.getAll(token, {
          page: currentPage,
          limit,
        });

        if (res.data) {
          setRoles(res.data);
          setTotal(res?.meta?.total ?? 0);
          setPage(currentPage);
        }
      }
    } catch (error) {
      setIsLoading(false);
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user && token) {
      getRolesTable(page);
    }
  }, [user, token, page]);

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-[#E5E7EB] overflow-hidden">
        <EsqueletoTabla filas={6} columnas={4} />
      </div>
    );
  }

  if (
    userHasPermission(
      "generales.catalogos.roles.read",
      user?.rbac?.effectivePermissions || []
    ) === false
  ) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <PermissionsDenyPage />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f9fafb] mt-3">
      <div className="w-full">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <TituloDePantalla nivel="interna">
                  Gestión de Roles
                </TituloDePantalla>
              </div>
            </div>

            {userHasPermission(
              "generales.catalogos.roles.create",
              user?.rbac?.effectivePermissions || []
            ) && (
              <button
                onClick={() => navigate("/roles/crear")}
                className={estiloDeBoton('primario')}
              >
                <CirclePlus className="w-4 h-4" />
                Nuevo Rol
              </button>
            )}
          </div>
        </div>

        {/* Empty State */}
        {!isLoading && roles?.length === 0 && (
          <div className="min-h-[400px] bg-[#ffffff] border border-gray-200 rounded-lg p-4 flex flex-col items-center justify-center">
            <img src={EmptyIcon} />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Aún no tienes datos para mostrar
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {roles?.length == 0
                ? "Empieza creando un nuevo rol para asignar permisos y organizar a tus usuarios."
                : "Intenta ajustar los filtros de búsqueda"}
            </p>
          </div>
        )}

        {/* Roles Table */}
        {roles.length > 0 && (
          <>
            <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-[#E5E7EB]">
                  <thead className="bg-[#FAF9FF]">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                        Rol
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                        Descripción
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                        Fecha de creación
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-medium text-[#1B2128] tracking-wider">
                        Estado
                      </th>
                      <th className="px-6 py-4 text-right text-sm font-medium text-[#1B2128] tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-[#ffffff] divide-y divide-[#E1E1E1]">
                    {roles.map((rol) => (
                      <tr
                        key={rol.id}
                        className="hover:bg-[#F4F7FB] transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap cursor-pointer"
                         onClick={() =>
                              navigate(`/roles/consultar?id=${rol.id}`)
                            }
                        >
                          <div className="flex items-center">
                            <div>
                              <div className="text-sm font-medium text-[#1B2128]">
                                {rol.name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap cursor-pointer"
                         onClick={() =>
                              navigate(`/roles/consultar?id=${rol.id}`)
                            }
                        >
                          <div className="relative group max-w-[160px]">
                            <p className="text-sm text-gray-900 dark:text-white truncate">
                              {rol.description ?? "-----"}
                            </p>

                            {/* Tooltip */}
                            <span className="absolute left-0 bottom-full mb-1 hidden group-hover:block whitespace-nowrap bg-black text-white text-xs py-1 px-2 rounded shadow-lg z-50">
                              {rol.description ?? "-----"}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap cursor-pointer"
                         onClick={() =>
                              navigate(`/roles/consultar?id=${rol.id}`)
                            }
                        >
                          <div className="text-sm text-gray-900 dark:text-white">
                            {rol?.createdAt
                              ? formatDate(rol?.createdAt)
                              : ""}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center cursor-pointer"
                         onClick={() =>
                              navigate(`/roles/consultar?id=${rol.id}`)
                            }
                        >
                          <div className="flex justify-center items-center w-[140px]">
                            <SwitchCustom
                              value={rol.isActive ?? true}
                              onChange={() => {}}
                              disabled
                            />
                            <span className="ml-2 text-sm font-medium text-gray-900 dark:text-white">
                              {rol.isActive ? "Activo" : "Inactivo"}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-4">
                          {userHasPermission(
                            "generales.catalogos.roles.update",
                            user?.rbac?.effectivePermissions || []
                          ) && (
                            <button
                              onClick={() =>
                                navigate(`/roles/editar?id=${rol.id}`)
                              }
                              className="text-[#315FB9] hover:text-[#315FB9]/80 hover:underline inline-flex items-center space-x-1 text-sm font-semibold"
                            >
                              <Pen className="h-4 w-4" />
                              <span>Editar</span>
                            </button>
                          )}
                          <button
                            onClick={() =>
                              navigate(`/roles/consultar?id=${rol.id}`)
                            }
                            className="text-[#315FB9] hover:text-[#315FB9]/80 hover:underline inline-flex items-center space-x-1 text-sm font-semibold"
                          >
                            <Eye className="h-4 w-4" />
                            <span>Ver</span>
                          </button>
                          {rol.key !== "MASTER" &&
                            userHasPermission(
                              "generales.catalogos.roles.delete",
                              user?.rbac?.effectivePermissions || []
                            ) && (
                              <button
                                onClick={() => setPorEliminar(rol)}
                                className="text-[#EF4444] hover:text-[#EF4444]/80 hover:underline inline-flex items-center space-x-1 text-sm font-semibold"
                              >
                                <Trash2 className="h-4 w-4" />
                                <span>Eliminar</span>
                              </button>
                            )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-6 py-4 border-t border-[#E5E7EB] bg-white">
                    <span className="text-sm text-gray-600">
                      Página {page} de {totalPages}
                    </span>

                    <div className="flex items-center gap-2">
                      <button
                        disabled={page === 1}
                        onClick={() => setPage((prev) => prev - 1)}
                        className={`px-3 py-1 rounded-md text-sm border ${
                          page === 1
                            ? "text-gray-400 border-gray-200 cursor-not-allowed"
                            : "text-[#315FB9] border-[#315FB9] hover:bg-[#315FB9]/10"
                        }`}
                      >
                        Anterior
                      </button>

                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                        (pageNumber) => (
                          <button
                            key={pageNumber}
                            onClick={() => setPage(pageNumber)}
                            className={`px-3 py-1 rounded-md text-sm border ${
                              page === pageNumber
                                ? "bg-[#315FB9] text-white border-[#315FB9]"
                                : "text-[#315FB9] border-[#315FB9] hover:bg-[#315FB9]/10"
                            }`}
                          >
                            {pageNumber}
                          </button>
                        )
                      )}

                      <button
                        disabled={page === totalPages}
                        onClick={() => setPage((prev) => prev + 1)}
                        className={`px-3 py-1 rounded-md text-sm border ${
                          page === totalPages
                            ? "text-gray-400 border-gray-200 cursor-not-allowed"
                            : "text-[#315FB9] border-[#315FB9] hover:bg-[#315FB9]/10"
                        }`}
                      >
                        Siguiente
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {porEliminar && (
        <ConfirmarEliminacion
          titulo="¿Eliminar este rol?"
          nombre={porEliminar.name}
          detalle="El rol deja de aparecer y se puede volver a usar su clave. Lo que ya quedó registrado en el historial se conserva. Si algún usuario todavía lo tiene, no se podrá eliminar."
          trabajando={eliminando}
          onCancelar={() => setPorEliminar(null)}
          onConfirmar={() => void eliminarRol()}
        />
      )}
    </div>
  );
};
